import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { twJoin, twMerge } from 'tailwind-merge';
import { formatNumber, formatNumberWithDecimals } from '../../../lib/formatters';
import { Gwei } from '../../../lib/gwei';

// Trade form stat row.
export const StatRow: React.FC<{
  label: React.ReactNode;
  labelClassName?: string;
  childrenWrapperClassName?: string;
  children?: React.ReactNode;
}> = ({ label, labelClassName, childrenWrapperClassName, children }) => {
  return (
    <div className="flex items-center justify-between">
      <span className={twMerge('text-2xs text-contentSecondary', labelClassName)}>{label}</span>
      <div
        className={twMerge(
          'flex items-center gap-2 text-2xs text-contentTertiary',
          childrenWrapperClassName
        )}
      >
        {children}
      </div>
    </div>
  );
};

// Default stat row value that formats the data using the provided formatter.
export const StatRowValue: React.FC<{
  value: bigint | undefined;
  // Number to base diff colors on. Defaults to value.
  relativeValue?: bigint;
  colorised?: boolean;
  reverseColor?: boolean;
  title?: string;
  formatValue: (value: bigint) => string;
  className?: string;
}> = ({ value, relativeValue = value, title, colorised, reverseColor, formatValue, className }) => {
  const colorClass =
    !relativeValue || !colorised
      ? ''
      : relativeValue > 0n
        ? reverseColor
          ? 'text-contentRed'
          : 'text-contentGreen'
        : reverseColor
          ? 'text-contentGreen'
          : 'text-contentRed';

  return (
    <span className={twMerge(colorClass, className)} title={title}>
      {value !== undefined ? formatValue(value) : '-'}
    </span>
  );
};

export const StatRowValueDiff: React.FC<{
  currentValue?: bigint;
  projectedValue?: bigint;
  colorised?: boolean;
  reverseColor?: boolean;
  currentClassName?: string;
  projectedClassName?: string;
  formatValue: (value: bigint) => string;
}> = ({
  currentValue,
  projectedValue,
  colorised,
  reverseColor,
  currentClassName,
  projectedClassName,
  formatValue,
}) => {
  const showProjection = projectedValue !== undefined;

  return (
    <span>
      <StatRowValue
        title={formatNumberWithDecimals(currentValue)}
        formatValue={formatValue}
        value={currentValue}
        className={currentClassName}
      />
      {showProjection ? (
        <>
          <ArrowRightIcon className="mx-1 inline size-3 shrink-0" />
          <StatRowValue
            title={formatNumber(projectedValue)}
            formatValue={formatValue}
            value={projectedValue}
            className={projectedClassName}
            relativeValue={projectedValue - (currentValue ?? 0n)}
            colorised={colorised}
            reverseColor={reverseColor}
          />
        </>
      ) : null}
    </span>
  );
};

export const StatRowPercentBar = ({
  value,
  projectedValue,
  reverseColor = false,
  className,
}: {
  value: bigint;
  projectedValue?: bigint;
  reverseColor?: boolean;
  className?: string;
}) => {
  const boundedValue = Gwei.min(Gwei.max(value, 0n), Gwei.toGwei('1'));
  const boundedProjected = projectedValue
    ? Gwei.min(Gwei.max(projectedValue, 0n), Gwei.toGwei('1'))
    : boundedValue;

  // Calculate percentages from Gwei values
  const valuePercentage = Gwei.fromGwei(boundedValue);
  const projectedPercentage = Gwei.fromGwei(boundedProjected);

  const isHigher = boundedProjected > boundedValue;
  const baseLength = isHigher ? valuePercentage : projectedPercentage;
  const diffLength = Math.abs(projectedPercentage - valuePercentage);

  const progressBackground =
    'bg-' + isHigher
      ? reverseColor
        ? 'contentRed'
        : 'contentGreen'
      : reverseColor
        ? 'contentGreen'
        : 'contentRed';

  return (
    <div className={twMerge('relative h-1.5 w-12 rounded-sm bg-backgroundPrimary', className)}>
      <div
        className={twJoin(
          'absolute left-0 top-0 h-full bg-[theme(textColor.contentBlue)]',
          'rounded-sm',
          diffLength > 0n && 'rounded-r-none',
          boundedProjected < boundedValue
            ? `${progressBackground} border border-r-0 border-contentBlue`
            : ''
        )}
        style={{ width: `${baseLength * 100}%` }}
        role="progressbar"
        aria-valuenow={valuePercentage}
        aria-valuemin={0}
        aria-valuemax={100}
      />

      {diffLength > 0n && (
        <div
          className={twJoin(
            'absolute top-0 h-full',
            'rounded-sm rounded-l-none',
            isHigher
              ? `${progressBackground}`
              : `border border-l-0 border-[theme(textColor.contentBlue)]`
          )}
          style={{
            left: `${baseLength * 100}%`,
            width: `${diffLength * 100}%`,
          }}
        />
      )}
    </div>
  );
};
